import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { t } from "i18next";

import RegistrationDialog from "src/components/custom-dialog/registration-dialog";
import { useAuthContext } from "src/auth/hooks";
import { usePlacenetHandshake } from "src/api/placenet";
import {
  resetPlacenetAuthFailure,
  setToTruePlacenetAuthFailure,
  usePlacenetAuth,
} from "src/sections/auth/3rdparty/placenetAuth";

// ----------------------------------------------------------------------

export default function PlacenetAuthentication() {
  const navigate = useNavigate();
  const placenetToken = usePlacenetAuth();

  const {
    handshake,
    handshakeLoading,
    handshakeError,
    isExistingUser,
    isFirstTimeUser,
  } = usePlacenetHandshake(placenetToken);

  const { loginWithAccessToken } = useAuthContext();

  const shownExistingDialogRef = useRef(false);
  const shownFirstTimeDialogRef = useRef(false);
  const lastHandshakedTokenRef = useRef<string | null>(null);

  const [loginDone, setLoginDone] = useState(false);
  const [showExistingDialog, setShowExistingDialog] = useState(false);
  const [showFirstTimeDialog, setShowFirstTimeDialog] = useState(false);
  const [showAvatarName, setShowAvatarName] = useState("");
  const [placenetAuthFailed, setPlacenetAuthFailed] = useState(false);

  // 1) Auto-login existing user
  useEffect(() => {
    if (isExistingUser && handshake?.accessToken && !loginDone) {
      loginWithAccessToken(handshake.accessToken);
      setLoginDone(true);
    }
  }, [isExistingUser, handshake?.accessToken, loginDone, loginWithAccessToken]);

  // 2) After login → navigate into the normal app
  useEffect(() => {
    if (loginDone && isExistingUser) {
      // choose where you want them to land:
      // navigate(PATH_AFTER_LOGIN, { replace: true });
      // navigate("/", { replace: true });
    }
  }, [loginDone, isExistingUser, navigate]);

  // 3) Existing user UX dialog
  useEffect(() => {
    if (!isExistingUser || !handshake) return;
    if (shownExistingDialogRef.current) return;

    shownExistingDialogRef.current = true;
    setShowAvatarName(`${handshake.user.firstName} ${handshake.user.lastName}`);

    // Enable this if you want to show the existing-user welcome dialog.
    // setShowExistingDialog(true);
  }, [isExistingUser, handshake]);

  // 4) First-time user UX dialog
  useEffect(() => {
    if (!isFirstTimeUser || !handshake) return;
    if (shownFirstTimeDialogRef.current) return;

    shownFirstTimeDialogRef.current = true;
    setShowAvatarName(handshake.identity.avatarName);
    setShowFirstTimeDialog(true);
  }, [isFirstTimeUser, handshake]);

  // 5) Avoid duplicate token handling
  useEffect(() => {
    if (!placenetToken) return;
    if (lastHandshakedTokenRef.current === placenetToken) return;

    lastHandshakedTokenRef.current = placenetToken;
  }, [placenetToken]);

  // 6) Authentication failure handling
  useEffect(() => {
    const status =
      (handshakeError as any)?.status ||
      (handshakeError as any)?.response?.status;

    const detail =
      (handshakeError as any)?.detail ||
      (handshakeError as any)?.response?.data?.detail;

    if (
      status === 401 ||
      (typeof detail === "string" && detail.startsWith("placenet_"))
    ) {
      delete (window as any).__authToken;
      setToTruePlacenetAuthFailure();
      setPlacenetAuthFailed(true);
    }
  }, [handshakeError]);

  if (handshakeLoading) {
    return <div>{t("placenet_checking_access")}</div>;
  }

  if (placenetAuthFailed) {
    return (
      <Dialog open fullWidth>
        <DialogTitle>{t("authentication_failed")}</DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {t("placenet_invalid_or_expired_token")}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setPlacenetAuthFailed(false);
              resetPlacenetAuthFailure();

              try {
                window.parent.postMessage("READY", "*");
              } catch {}
            }}
          >
            {t("retry")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={showExistingDialog}
        onClose={() => setShowExistingDialog(false)}
        fullWidth
      >
        <DialogTitle>{t("existing_user")}</DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {t("welcome")}
          <br />
          {showAvatarName}
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth
        open={showFirstTimeDialog}
        onClose={() => setShowFirstTimeDialog(false)}
        disableEscapeKeyDown
      >
        <DialogTitle>{t("welcome")}</DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <p style={{ marginBottom: 16 }}>
            {t("placenet_first_time_register_intro", {
              name: showAvatarName,
            })}
          </p>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <RegistrationDialog />
        </DialogActions>
      </Dialog>
    </>
  );
}
